import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
    AsyncValidatorFn,
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { Customer, DynamicField } from '@cargos/sprintpay-models';
import { Validation } from '@cargos/sprintpay-models/dist/src/dynamic-values/dynamic-fields';
import { CustomPatterns, CustomValidators } from '@cargos/sprintpay-utils';
import { Subject, takeUntil } from 'rxjs';
import { DynamicFieldsForPayment } from 'src/app/shared/services';
import { CustomerService } from 'src/app/shared/services/customer.service';
import { DynamicFieldComponent } from '../dynamic-field/dynamic-field.component';

@Component({
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, DynamicFieldComponent],
    selector: 'app-dynamic-form',
    templateUrl: './dynamic-form.component.html',
    styleUrls: ['./dynamic-form.component.scss'],
})
export class DynamicFormComponent implements OnInit, OnDestroy {
    public dynamicFormGroup: FormGroup;
    public dynamicFields: DynamicField[] = [];
    private unsubscribe$ = new Subject<void>();

    @Input() modelForm: DynamicFieldsForPayment = {};
    @Input() customerReferenceRequired = true;

    private readonly customerService = inject(CustomerService);

    // Customer model properties allowed as prefill sources. Keep this whitelist tight:
    // resolution is restricted to these keys so arbitrary customer data can never leak.
    private readonly prefillableCustomerKeys: ReadonlyArray<keyof Customer> = [
        'email',
        'notificationEmail',
        'firstName',
        'lastName',
        'companyName',
        'phone',
    ];

    ngOnInit(): void {
        this.dynamicFields = this.proccessFields(this.modelForm?.fields || []);
        this.buildForm();
        this.setupDependencyFieldListeners();
        this.setupCustomerRefValidation();
        this.applyCustomerReferenceRequirement();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    buildForm(): void {
        const formGroupFields = this.getFormControlsFields();
        this.dynamicFormGroup = new FormGroup(formGroupFields);
    }

    getFormControlsFields(): { [key: string]: FormControl<string> } {
        const formGroupFields = {};

        this.dynamicFields.forEach((field: DynamicField) => {
            const fieldProps: DynamicField = this.proccessField(field);
            const validators: ValidatorFn[] | null = this.getValidators(fieldProps.validations || []);
            const asyncValidators: AsyncValidatorFn[] | null = this.getAsyncValidators(fieldProps.validations || []);

            if (field.name) {
                const isAutoPopulated: boolean = field.isDynamic === true && !!field.value;
                const initialValue: string | null = isAutoPopulated
                    ? this.resolvePrefillValue(field.value!)
                    : (field.value ?? '');

                formGroupFields[field.name] = new FormControl(initialValue ?? '', validators, asyncValidators);
            }
        });

        return formGroupFields;
    }

    // Resolves a prefill value dynamically from the customer model, limited to the whitelist above.
    private resolvePrefillValue(key: string): string | null {
        // Guests have no personal account data, so nothing should be pre-filled for them.
        if (this.customerService.isGuest()) {
            return null;
        }

        if (!this.prefillableCustomerKeys.includes(key as keyof Customer)) {
            return null;
        }

        const value = this.customerService.getCustomer()?.[key as keyof Customer];

        return typeof value === 'string' && value.trim() !== '' ? value : null;
    }

    getValidators(validations: Validation[]): ValidatorFn[] | null {
        if (!validations) {
            return null;
        }

        const validators: ValidatorFn[] = [];

        validations?.forEach(({ value, name }) => {
            switch (name) {
                case 'min':
                    validators.push(Validators.min(Number(value)));
                    break;
                case 'max':
                    validators.push(Validators.max(Number(value)));
                    break;
                case 'required':
                    if (value === 'true') {
                        validators.push(Validators.required);
                    }
                    break;
                case 'requiredTrue':
                    if (value === 'true') {
                        validators.push(Validators.requiredTrue);
                    }
                    break;
                case 'email':
                    if (value === 'true') {
                        validators.push(CustomValidators.regex(new RegExp(CustomPatterns.EMAIL)));
                    }
                    break;
                case 'minlength':
                    validators.push(Validators.minLength(Number(value)));
                    break;
                case 'maxlength':
                    validators.push(Validators.maxLength(Number(value)));
                    break;
                case 'pattern':
                    let patterns: string | RegExp | string[] | RegExp[] = [];
                    if (Array.isArray(value)) {
                        patterns = value
                            .map((pattern: string | RegExp) => {
                                if (pattern instanceof RegExp) {
                                    return pattern;
                                }
                                if (typeof pattern === 'string') {
                                    const cleaned = (pattern as string).replace(/^\/|\/$/g, '');
                                    try {
                                        return new RegExp(cleaned);
                                    } catch {
                                        return null;
                                    }
                                }
                                return null;
                            })
                            .filter((p): p is RegExp => p instanceof RegExp);
                    } else if (value instanceof RegExp) {
                        patterns = value;
                    } else {
                        let newPattern = '';
                        if (typeof value === 'string') {
                            newPattern = (value as string).replace(/^\/|\/$/g, '');
                        }
                        try {
                            patterns = new RegExp(newPattern);
                        } catch {
                            patterns = new RegExp('');
                        }
                    }

                    validators.push(CustomValidators.regex(patterns, 'pattern'));
                    break;
                case 'nullValidator':
                    if (value === 'true') {
                        validators.push(Validators.nullValidator);
                    }
                    break;
                case 'preventedHTMLStr':
                    validators.push(CustomValidators.preventHTMLContent());
                    break;
                default:
                    break;
            }
        });

        return validators;
    }

    getAsyncValidators(validations: Validation[]): AsyncValidatorFn[] | null {
        if (!validations) {
            return null;
        }

        const asyncValidators: AsyncValidatorFn[] = [];

        validations?.forEach(({ value, name }) => {
            switch (name) {
                case 'awbValidator':
                    asyncValidators.push(CustomValidators.awbValidator());
                    break;
                default:
                    break;
            }
        });

        return asyncValidators;
    }

    proccessFields(fields: DynamicField[]): DynamicField[] {
        return fields.map((field: DynamicField) => {
            return this.proccessField(field);
        });
    }

    proccessField(field: DynamicField): DynamicField {
        const newField = { ...field };

        if (Array.isArray(newField.validations)) {
            newField.validations = this.processValidations(newField.validations, newField.fieldType);
        }

        return newField;
    }

    processValidations(validations: Validation[], fieldType?: string): Validation[] {
        const validationsResult: Validation[] = this.parsePatternValues(validations);

        const hasMaxLength = validationsResult.some((validation) => validation.name === 'maxlength');

        const excludedTypes: string[] = ['dropdown', 'autoComplete', 'checkbox', 'radioButton'];

        const isExcludedType: boolean = excludedTypes.includes(fieldType ?? '');

        if (!hasMaxLength && !isExcludedType) {
            validationsResult.push({
                id: validationsResult.length + 1,
                name: 'maxlength',
                value: '255',
                message: 'Maximum length exceeded (255 characters maximum)',
                type: 'VALIDATION',
                apiValidator: undefined,
                params: undefined,
                title: undefined,
                typeRequest: undefined,
            });
        }

        return validationsResult;
    }

    parsePatternValues(validations: Validation[]): Validation[] {
        const result = validations.map((validation) => {
            const newValidation = { ...validation };
            if (newValidation.name === 'pattern') {
                if (typeof newValidation.value === 'string') {
                    let valueStr = newValidation.value.trim();
                    if (valueStr.startsWith('[') && valueStr.endsWith(']')) {
                        try {
                            const parsed = JSON.parse(valueStr);
                            if (Array.isArray(parsed)) {
                                newValidation.value = parsed
                                    .map((patternString) => {
                                        if (typeof patternString === 'string') {
                                            if (patternString.startsWith('/') && patternString.endsWith('/')) {
                                                patternString = patternString.slice(1, -1);
                                            }
                                            try {
                                                return new RegExp(patternString);
                                            } catch {
                                                return null;
                                            }
                                        }
                                        return null;
                                    })
                                    .filter(
                                        (patternRegExp): patternRegExp is RegExp => patternRegExp instanceof RegExp
                                    );
                                return newValidation;
                            }
                        } catch (e) {}

                        valueStr = valueStr.slice(1, -1);
                        const patternStrings = valueStr
                            .split(',')
                            .map((pattern) => pattern.trim())
                            .filter(Boolean);
                        newValidation.value = patternStrings
                            .map((patternString) => {
                                if (
                                    (patternString.startsWith('"') && patternString.endsWith('"')) ||
                                    (patternString.startsWith("'") && patternString.endsWith("'"))
                                ) {
                                    patternString = patternString.slice(1, -1);
                                }
                                if (patternString.startsWith('/') && patternString.endsWith('/')) {
                                    patternString = patternString.slice(1, -1);
                                }
                                try {
                                    return new RegExp(patternString);
                                } catch {
                                    return null;
                                }
                            })
                            .filter((patternRegExp): patternRegExp is RegExp => patternRegExp instanceof RegExp);
                    } else {
                        let patternString = valueStr;
                        if (patternString.startsWith('/') && patternString.endsWith('/')) {
                            patternString = patternString.slice(1, -1);
                        }
                        try {
                            newValidation.value = new RegExp(patternString);
                        } catch {
                            newValidation.value = patternString;
                        }
                    }
                }
            }
            return newValidation;
        });

        return result;
    }

    private setupCustomerRefValidation(): void {
        const customerRefControl = this.dynamicFormGroup.get('customerRef');
        const customerReference2Control = this.dynamicFormGroup.get('customerReference2');

        if (!customerRefControl || !customerReference2Control) {
            return;
        }

        const createAtLeastOneValidator = (otherControlName: string): ValidatorFn => {
            return (control): ValidationErrors | null => {
                const otherControl = this.dynamicFormGroup.get(otherControlName);
                if (!otherControl) {
                    return null;
                }

                const currentHasValue = this.hasValue(control.value);
                const otherHasValue = this.hasValue(otherControl.value);

                if (!currentHasValue && !otherHasValue) {
                    return { atLeastOneRequired: true };
                }
                return null;
            };
        };

        customerRefControl.addValidators(createAtLeastOneValidator('customerReference2'));
        customerReference2Control.addValidators(createAtLeastOneValidator('customerRef'));

        customerRefControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            customerReference2Control.updateValueAndValidity({ emitEvent: false });
        });

        customerReference2Control.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            customerRefControl.updateValueAndValidity({ emitEvent: false });
        });

        customerRefControl.updateValueAndValidity({ emitEvent: false });
        customerReference2Control.updateValueAndValidity({ emitEvent: false });
    }

    private applyCustomerReferenceRequirement(): void {
        const customerRefControl = this.dynamicFormGroup.get('customerRef');

        if (!customerRefControl || this.customerReferenceRequired) {
            return;
        }

        customerRefControl.removeValidators(Validators.required);
        customerRefControl.updateValueAndValidity({ emitEvent: false });
    }

    private setupDependencyFieldListeners(): void {
        if (!this.modelForm?.fields) {
            return;
        }

        const fieldMap = new Map<number, DynamicField>();
        const dependencyGroups = new Map<number, DynamicField[]>();

        this.modelForm.fields.forEach((field) => {
            if (field.id !== undefined) {
                fieldMap.set(field.id, field);
            }
            if (field.dependentFieldId !== undefined && !field.dependentFieldDisabled) {
                if (!dependencyGroups.has(field.dependentFieldId)) {
                    dependencyGroups.set(field.dependentFieldId, []);
                }
                dependencyGroups.get(field.dependentFieldId)!.push(field);
            }
        });

        dependencyGroups.forEach((dependentFields, mainFieldId) => {
            const mainField = fieldMap.get(mainFieldId);
            if (!mainField?.name) {
                return;
            }
            const mainControl = this.dynamicFormGroup.get(mainField.name);

            if (mainControl) {
                mainControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
                    const hasValue = this.hasValue(value);

                    dependentFields.forEach((depField) => {
                        if (!depField.name) {
                            return;
                        }
                        const depControl = this.dynamicFormGroup.get(depField.name);
                        if (depControl) {
                            if (hasValue) {
                                depControl.disable({ emitEvent: false });
                                depControl.setValue('', { emitEvent: false });
                            } else {
                                depControl.enable({ emitEvent: false });
                            }
                        }
                    });
                });

                dependentFields.forEach((depField) => {
                    const depControl = this.dynamicFormGroup.get(depField.name!);

                    if (depControl) {
                        depControl.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((value) => {
                            const hasValue = this.hasValue(value);

                            if (hasValue) {
                                mainControl.disable({ emitEvent: false });
                                mainControl.setValue('', { emitEvent: false });
                            } else {
                                const otherDependentHasValue = dependentFields.some((otherField) => {
                                    if (otherField.name === depField.name) {
                                        return false;
                                    }
                                    const otherControl = this.dynamicFormGroup.get(otherField.name!);
                                    const otherValue = otherControl?.value;
                                    return this.hasValue(otherValue);
                                });

                                if (!otherDependentHasValue) {
                                    mainControl.enable({ emitEvent: false });
                                }
                            }
                        });
                    }
                });
            }
        });
    }

    private hasValue(value: string): boolean {
        return value !== null && value !== undefined && value !== '';
    }
}
